import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiUrl } from "../config/api";
import { resolveAccess } from "../utils/access";

// Поднимем функцию форматирования месяца выше использования,
// чтобы избежать возможных предупреждений линтера
function monthName(m) {
  const names = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const i = Math.max(1, Math.min(12, Number(m||0))) - 1;
  return names[i] || String(m);
}

function monthsBetween(fromMonth, fromYear, toMonth, toYear) {
  const result = [];
  if (!fromMonth || !fromYear || !toMonth || !toYear) return result;
  let month = Number(fromMonth);
  let year = Number(fromYear);
  const endMonth = Number(toMonth);
  const endYear = Number(toYear);
  while (year < endYear || (year === endYear && month <= endMonth)) {
    result.push({ month, year });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return result;
}

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_BASE = apiUrl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [group, setGroup] = useState(null);
  const [edit, setEdit] = useState({ name: "", description: "" });
  // Глобальные состояния копирования и добавления больше не нужны —
  // копирование теперь расположено прямо в строках календаря
  const [calendar, setCalendar] = useState({ months: [] });
  // Сводный расчёт теперь открывается на отдельной странице (в новой вкладке)
  const [objects, setObjects] = useState([]);
  const [repair, setRepair] = useState({ objectId: null, fromMonth: 1, fromYear: new Date().getFullYear(), toMonth: 12, toYear: new Date().getFullYear() });
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem('authUser') || 'null'); } catch { return null; } });
  const access = useMemo(() => resolveAccess(user), [user]);
  const isSuper = access.isSuper;
  const canCloseGroup = access.hasGdRole || access.isSuper;
  const [assigningKey, setAssigningKey] = useState(null);
  const [clearingKey, setClearingKey] = useState(null);
  const [objectLinks, setObjectLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState('');
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeForm, setCloseForm] = useState({ month: '', year: '' });

  const load = React.useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/api/card-groups/${id}`)
      .then((r) => r.json())
      .then((g) => {
        setGroup(g);
        try {
          const title = g?.name ? `Группа: ${g.name} (ID ${g.id})` : `Группа ${g?.id || ''}`;
          document.title = title;
        } catch {}
        setEdit({ name: g?.name || "", description: g?.description || "" });
        setLoading(false);
      })
        .catch(() => { setError("Ошибка загрузки группы"); setLoading(false); });
      }, [id, API_BASE]);

  const loadCalendar = React.useCallback(() => {
    fetch(`${API_BASE}/api/card-groups/${id}/calendar`)
      .then(r => r.json())
      .then(async (data) => {
        const base = data || { months: [] };
        // Если сервер по какой-то причине не вернул кандидатов — подстрахуемся на клиенте
        const empty = (base.months || []).every(m => !m.candidates || m.candidates.length === 0);
        if (empty && group?.objectId) {
          try {
            const list = await fetch(`${API_BASE}/api/card-calc?full=1&perPage=1000`).then(r=>r.json());
            const items = Array.isArray(list.items) ? list.items : [];
            const ofObj = items.filter(c => Number(c.objectId) === Number(group.objectId));
            const byKey = new Map();
            for (const c of ofObj) {
              const hasPeriod = c.periodYear != null && c.periodMonth != null;
              if (hasPeriod) {
                const k = `${c.periodYear}-${c.periodMonth}`;
                if (!byKey.has(k)) byKey.set(k, []);
                byKey.get(k).push({ id: c.id, objectCodeFull: c.objectCodeFull, objectCode: c.objectCode, status: c.status, company: c.company });
              } else {
                // unassigned: добавим в каждую строку потом
              }
            }
            const unassigned = ofObj.filter(c => c.periodYear == null || c.periodMonth == null)
              .map(c => ({ id: c.id, objectCodeFull: c.objectCodeFull, objectCode: c.objectCode, status: c.status, company: c.company }));
            base.months = (base.months || []).map(m => ({
              ...m,
              candidates: [ ...(byKey.get(`${m.year}-${m.month}`) || []), ...unassigned ]
            }));
          } catch {}
        }
        setCalendar(base);
      })
        .catch(()=> setCalendar({ months: [] }));
      }, [id, group, API_BASE]);

  useEffect(() => { load(); }, [load]);
  // после загрузки группы подгружаем календарь, чтобы в фолбэке был objectId
  useEffect(() => { if (group && group.id) loadCalendar(); }, [group, loadCalendar]);
    useEffect(() => {
      if (!group) return;
      setCloseForm({
        month: group.closeFromMonth ? String(group.closeFromMonth) : String(group.toMonth || ''),
        year: group.closeFromYear ? String(group.closeFromYear) : String(group.toYear || ''),
      });
    }, [group]);
  useEffect(() => {
    if (!group?.objectId) {
      setObjectLinks([]);
      setLinksError('');
      setLinksLoading(false);
      return;
    }
    setLinksLoading(true);
    setLinksError('');
    fetch(`${API_BASE}/api/objects/${group.objectId}/links`)
      .then((resp) => resp.json().then((data) => ({ ok: resp.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.error || 'links_error');
        setObjectLinks(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        console.error('group links load failed', e);
        setObjectLinks([]);
        setLinksError('Не удалось загрузить ссылки объекта');
      })
      .finally(() => setLinksLoading(false));
  }, [group?.objectId, API_BASE]);

  // Load objects for repair dropdown when needed
  useEffect(() => {
    if (!group || (group.objectId && group.fromMonth && group.fromYear && group.toMonth && group.toYear)) return;
    fetch(`${API_BASE}/api/objects`).then(r=>r.json()).then(data => {
      const arr = Array.isArray(data) ? data : [];
      setObjects(arr);
      // try to preselect by name
      if (group && !repair.objectId) {
        const guess = arr.find(o => String(o.name).trim() === String(group.name).trim());
        setRepair(r => ({ ...r, objectId: guess?.id || null }));
      }
    }).catch(()=>{});
  }, [group, repair.objectId, API_BASE]);

  const saveMeta = () => {
    fetch(`${API_BASE}/api/card-groups/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(edit),
    })
      .then((r) => r.json())
      .then((_) => load())
      .catch((e) => alert("Ошибка сохранения: " + (e?.message || e)));
  };

  // глобальные add/copy функции удалены — копирование выполняется в строках календаря

  // Прямое назначение из списка больше не используется — оставлено копирование выбранной

  // Состояние модалки множественного копирования
  const [copyModal, setCopyModal] = useState({ open: false, objectCode: null, preselect: null });

  // Копирование выбранной карточки в несколько месяцев
  const copyCardToMonths = async (objectCode, months) => {
    if (!objectCode) return alert('Сначала выберите карточку в списке');
    if (!group || !group.objectId) return alert('Группа не привязана к объекту');
    try {
      for (const m of months) {
        const { month, year } = m;
        const newCard = await fetch(apiUrl(`/api/card-calc/${encodeURIComponent(String(objectCode))}/copy`), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetObjectId: group.objectId })
        }).then(r=>r.json());
        if (!newCard || !newCard.id) throw new Error('copy_failed');
        const resp = await fetch(`${API_BASE}/api/card-groups/${id}/select-month`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ month, year, cardId: newCard.id })
        });
        if (!resp.ok) {
          const err = await resp.json().catch(()=>null);
          throw new Error(err?.error || `HTTP ${resp.status}`);
        }
      }
      loadCalendar();
    } catch (e) {
      alert('Ошибка копирования: ' + (e?.message || e));
    }
  };

  // Упразднено: расчёт сводной таблицы вынесен на отдельную страницу

  const openSummary = () => {
    const url = `${window.location.origin}/groups/${id}/summary`;
    window.open(url, '_blank');
  };

  const sortedLinks = useMemo(() => {
    const list = Array.isArray(objectLinks) ? [...objectLinks] : [];
    return list.sort((a, b) => {
      const orderA = Number(a?.sortOrder) || 0;
      const orderB = Number(b?.sortOrder) || 0;
      if (orderA !== orderB) return orderA - orderB;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [objectLinks]);

  const closePeriodOptions = useMemo(() => {
    if (!group?.fromMonth || !group?.fromYear || !group?.toMonth || !group?.toYear) return [];
    return monthsBetween(group.fromMonth, group.fromYear, group.toMonth, group.toYear);
  }, [group?.fromMonth, group?.fromYear, group?.toMonth, group?.toYear]);

  const closeGroup = async () => {
    const month = Number(closeForm.month) || 0;
    const year = Number(closeForm.year) || 0;
    if (!month || !year) {
      alert('Выберите месяц и год закрытия группы');
      return;
    }
    setClosing(true);
    try {
      const resp = await fetch(`${API_BASE}/api/card-groups/${id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      setCloseModalOpen(false);
      load();
      loadCalendar();
    } catch (e) {
      alert('Ошибка закрытия группы: ' + (e?.message || e));
    } finally {
      setClosing(false);
    }
  };

  const assignCardToMonth = async (month, year, candidate, mismatch, currentAssigned) => {
    if (!candidate?.id) {
      alert('Сначала выберите карточку из списка');
      return;
    }
    if (!mismatch && currentAssigned?.id === candidate.id) {
      // Ничего не меняем, просто сбрасываем ручной ввод
      setCalendar((prev) => ({
        months: prev.months.map((m) => (m.year === year && m.month === month ? { ...m, _q: '' } : m)),
      }));
      return;
    }
    if (mismatch) {
      const ok = window.confirm('Карточка относится к другому месяцу. Перенести её в выбранный период?');
      if (!ok) return;
    }
    const key = `${year}-${month}`;
    setAssigningKey(key);
    try {
      const resp = await fetch(`${API_BASE}/api/card-groups/${id}/select-month`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year, cardId: candidate.id })
      });
      const data = await resp.json().catch(()=>null);
      if (!resp.ok) {
        const msg = data?.error ? `Код ошибки: ${data.error}` : `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      setCalendar((prev) => ({
        months: prev.months.map((m) => (m.year === year && m.month === month ? { ...m, _q: '' } : m)),
      }));
      loadCalendar();
    } catch (e) {
      alert('Ошибка назначения: ' + (e?.message || e));
    } finally {
      setAssigningKey(null);
    }
  };

  const clearMonthSelection = async (month, year) => {
    const key = `${year}-${month}`;
    setClearingKey(key);
    try {
      const resp = await fetch(`${API_BASE}/api/card-groups/${id}/clear-month`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year })
      });
      const data = await resp.json().catch(()=>null);
      if (!resp.ok) {
        const msg = data?.error ? `Код ошибки: ${data.error}` : `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      setCalendar((prev) => ({
        months: prev.months.map((m) => (m.year === year && m.month === month ? { ...m, _q: '' } : m)),
      }));
      loadCalendar();
    } catch (e) {
      alert('Ошибка удаления карточки: ' + (e?.message || e));
    } finally {
      setClearingKey(null);
    }
  };

  const signGroup = async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/card-groups/${id}/sign`, { method: 'POST' });
      const data = await resp.json().catch(()=>null);
      if (!resp.ok) {
        if (data?.error === 'incomplete_calendar') {
          throw new Error('Не все месяцы заполнены в календаре группы');
        }
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      load(); loadCalendar();
    } catch (e) {
      alert('Ошибка подписания группы: ' + (e?.message || e));
    }
  };

  const unsignGroup = async () => {
    if (!isSuper) { alert('Снятие подписи доступно только пользователям 1001/1002'); return; }
    try {
      const resp = await fetch(`${API_BASE}/api/card-groups/${id}/unsign`, { method: 'POST' });
      const data = await resp.json().catch(()=>null);
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      load(); loadCalendar();
    } catch (e) {
      alert('Ошибка снятия подписи: ' + (e?.message || e));
    }
  };

  // removeFromGroup больше не нужен — выбор идёт по месяцам

  return (
    <div style={{ padding: "32px 0 0 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={() => navigate(-1)} style={{ padding: "6px 10px" }}>← Назад</button>
        <h2 style={{ margin: 0 }}>Группа</h2>
        <div style={{ marginLeft: "auto", display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={openSummary} style={{ padding: '8px 12px', background: '#f6ecd4', border: '1px solid #e0b54a', borderRadius: 6 }}>Сводный расчёт</button>
          <Link to="/groups" style={{ alignSelf: 'center' }}>К журналу групп</Link>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}>Загрузка…</div>
      ) : error ? (
        <div style={{ textAlign: "center", color: "#c00" }}>{error}</div>
      ) : !group ? (
        <div style={{ textAlign: "center" }}>Группа не найдена</div>
      ) : (
        <>
          <div style={{ background: "#fff", padding: 16, borderRadius: 8, boxShadow: "0 1px 14px #0002", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, alignItems: "center" }}>
              <label style={{ textAlign: "right" }}>ID:</label>
              <div>{group.id}</div>
              <label style={{ textAlign: "right" }}>Объект:</label>
              <div>{group.name}</div>
              <label style={{ textAlign: "right" }}>Адрес:</label>
              <div>{group.objectAddress || '—'}</div>
              <label style={{ textAlign: "right" }}>Ссылки:</label>
              <div>
                {linksLoading ? (
                  <span style={{ color: '#64748b' }}>Загрузка…</span>
                ) : linksError ? (
                  <span style={{ color: '#b91c1c' }}>{linksError}</span>
                ) : sortedLinks.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {sortedLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{
                          padding: '4px 10px',
                          borderRadius: 999,
                          border: '1px solid #d7e3fc',
                          background: '#f8fbff',
                          textDecoration: 'none',
                          color: '#1d4ed8',
                          fontWeight: 600,
                        }}
                        title={link.url}
                      >
                        {link.title || link.url}
                      </a>
                    ))}
                  </div>
                ) : (
                  '—'
                )}
              </div>
              <label style={{ textAlign: "right" }}>Код группы:</label>
              <div>{group.groupCode || '—'}</div>
              <label style={{ textAlign: "right" }}>Описание:</label>
              <input value={edit.description} onChange={(e) => setEdit((x) => ({ ...x, description: e.target.value }))} />
              <label style={{ textAlign: "right" }}>Период:</label>
              <div>
                {(group.fromMonth && group.fromYear && group.toMonth && group.toYear)
                  ? `${monthName(group.fromMonth)} ${group.fromYear} — ${monthName(group.toMonth)} ${group.toYear}`
                  : '—'}
              </div>
              <label style={{ textAlign: "right" }}>Закрыта с периода:</label>
              <div>
                {(group.closeFromMonth && group.closeFromYear)
                  ? `${monthName(group.closeFromMonth)} ${group.closeFromYear}`
                  : '—'}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              {canCloseGroup && (
                <button onClick={() => setCloseModalOpen(true)} style={{ padding: "8px 12px", background: "#fff3cd", border: "1px solid #e6b800", borderRadius: 6 }}>Закрыть группу</button>
              )}
              <button onClick={saveMeta} style={{ padding: "8px 12px", background: "#e7f6d4", border: "1px solid #8bc34a", borderRadius: 6 }}>Сохранить</button>
            </div>
          </div>

          {(!group.objectId || !group.fromMonth || !group.fromYear || !group.toMonth || !group.toYear) && (
            <div style={{ background: "#fff8e1", padding: 16, borderRadius: 8, boxShadow: "0 1px 14px #0002", marginBottom: 16, border: '1px solid #f0d28a' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Группа требует настройки: выберите объект и период</div>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'center' }}>
                <label style={{ textAlign: 'right' }}>Объект:</label>
                <select value={repair.objectId || ''} onChange={(e)=> setRepair(r=> ({ ...r, objectId: Number(e.target.value)||null }))}>
                  <option value="">— выберите —</option>
                  {objects.map(o => (<option key={o.id} value={o.id}>{String(o.codeBase).padStart(4,'0')} {o.name}</option>))}
                </select>
                <label style={{ textAlign: 'right' }}>Период с:</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select value={repair.fromMonth} onChange={(e)=> setRepair(r=> ({ ...r, fromMonth: Number(e.target.value) }))}>
                    {Array.from({length:12}, (_,i)=>i+1).map(m => <option key={m} value={m}>{String(m).padStart(2,'0')}</option>)}
                  </select>
                  <input type="number" value={repair.fromYear} onChange={(e)=> setRepair(r=> ({ ...r, fromYear: Number(e.target.value) }))} style={{ width: 100 }} />
                </div>
                <label style={{ textAlign: 'right' }}>Период по:</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select value={repair.toMonth} onChange={(e)=> setRepair(r=> ({ ...r, toMonth: Number(e.target.value) }))}>
                    {Array.from({length:12}, (_,i)=>i+1).map(m => <option key={m} value={m}>{String(m).padStart(2,'0')}</option>)}
                  </select>
                  <input type="number" value={repair.toYear} onChange={(e)=> setRepair(r=> ({ ...r, toYear: Number(e.target.value) }))} style={{ width: 100 }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button onClick={async ()=>{
                  if (!repair.objectId) { alert('Выберите объект'); return; }
                  const d1 = new Date(repair.fromYear, repair.fromMonth-1, 1);
                  const d2 = new Date(repair.toYear, repair.toMonth-1, 1);
                  if (d2 < d1) { alert('Период некорректен'); return; }
                  try {
                    const resp = await fetch(`${API_BASE}/api/card-groups/${id}`, {
                      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ objectId: repair.objectId, fromMonth: repair.fromMonth, fromYear: repair.fromYear, toMonth: repair.toMonth, toYear: repair.toYear })
                    });
                    const ct = resp.headers.get('content-type') || '';
                    const data = ct.includes('application/json') ? await resp.json() : null;
                    if (!resp.ok) {
                      const msg = (data && data.error) ? data.error : `HTTP ${resp.status}`;
                      throw new Error(msg);
                    }
                    // Если всё ок, перезагрузим карточку и календарь
                    load(); loadCalendar();
                  } catch (e) {
                    alert('Ошибка сохранения: ' + (e?.message || e));
                  }
                }} style={{ padding: '8px 12px', background: '#fff3cd', border: '1px solid #f0d28a', borderRadius: 6 }}>Применить</button>
              </div>
            </div>
          )}

          <div id="copy-section" style={{ background: "#fff", padding: 16, borderRadius: 8, boxShadow: "0 1px 14px #0002", marginBottom: 16 }}>
            {/* Подписание группы */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, border: '1px dashed #c59bff', padding: '10px 12px', borderRadius: 10, background: '#faf7ff' }}>
              <span style={{ fontWeight: 700 }}>Подписание группы</span>

                        {closeModalOpen && (
                          <div onClick={() => !closing && setCloseModalOpen(false)} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: 20, borderRadius: 12, width: 480, maxWidth: '94%' }}>
                              <h3 style={{ marginTop: 0 }}>Закрыть группу</h3>
                              <div style={{ color: '#475569', marginBottom: 14 }}>
                                Выберите месяц и год, с которого группа перестанет быть актуальной. Все последующие карточки будут сняты с актуальности и перейдут в статус Согласован.
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'center' }}>
                                <label style={{ textAlign: 'right' }}>Период закрытия:</label>
                                <select value={`${closeForm.year}-${closeForm.month}`} onChange={(e) => {
                                  const [year, month] = String(e.target.value || '').split('-');
                                  setCloseForm({ month: month || '', year: year || '' });
                                }} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                                  <option value="">Выберите период</option>
                                  {closePeriodOptions.map((item) => (
                                    <option key={`${item.year}-${item.month}`} value={`${item.year}-${item.month}`}>
                                      {monthName(item.month)} {item.year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                                <button onClick={() => setCloseModalOpen(false)} disabled={closing} style={{ padding: '8px 12px' }}>Отмена</button>
                                <button onClick={closeGroup} disabled={closing || !closeForm.month || !closeForm.year} style={{ padding: '8px 12px', background: '#fff3cd', border: '1px solid #e6b800', borderRadius: 6 }}>
                                  {closing ? 'Закрываем…' : 'Закрыть группу'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
              <button onClick={signGroup} style={{ padding: '6px 10px', borderRadius: 6, background: '#e7f6d4', border: '1px solid #8bc34a' }}>Подписать все карточки</button>
              {isSuper && (
                <button onClick={unsignGroup} style={{ padding: '6px 10px', borderRadius: 6, marginLeft: 6 }}>Снять подписи</button>
              )}
              {isSuper && (
                <button
                  onClick={async ()=>{
                    try {
                      const url = new URL(`${API_BASE}/api/card-groups/${id}/actualize-all`);
                      let resp = await fetch(url.toString(), { method: 'POST' });
                      let data = await resp.json().catch(()=>null);
                      if (resp.status === 404) {
                        throw new Error('Эндпоинт не найден (404). Скорее всего, бэкенд запущен со старой версией кода. Перезапустите задачу "Run All" или backend, чтобы подхватить маршрут /actualize-all.');
                      }
                      if (resp.status === 409 && data?.error === 'group_overlap_actual') {
                        const overlaps = Array.isArray(data.overlaps) ? data.overlaps : [];
                        const parts = overlaps.map(g => {
                          const isNull = g.groupId == null;
                          const head = isNull
                            ? 'Карточки без группы'
                            : `Группа ${g.groupCode || g.groupId || ''}${g.name ? ` (${g.name})` : ''}`.trim();
                          const mlist = (Array.isArray(g.months) ? g.months : []).map(m => `\u2022 ${String(m.month).padStart(2,'0')}.${m.year} — актуальна ${m.objectCode || 'карточка'}`).join('\n');
                          return `${head}\n${mlist}`;
                        }).join('\n\n');
                        alert(`Нельзя актуализировать: пересечение периодов с уже актуальными группами.\n\n${parts}`);
                        return;
                      } else if (resp.status === 409 && data?.error === 'actual_exists') {
                        const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
                        const cnt = conflicts.length;
                        const details = conflicts.map((c) => {
                          const mm = String(c.month || '').padStart(2, '0');
                          const yy = c.year || '';
                          const exist = c.objectCode ? `уже актуальна карточка ${c.objectCode}` : 'уже есть актуальная карточка';
                          const tgt = c.target ? `; в этой группе назначена ${c.target}` : '';
                          return `• ${mm}.${yy} — ${exist}${tgt}`;
                        }).join('\n');
                        const msg = `Есть ${cnt} конфликт(ов) актуализации:\n${details}\n\nЗаменить на карточки этой группы?`;
                        const ok = window.confirm(msg);
                        if (!ok) return;
                        url.searchParams.set('force','1');
                        resp = await fetch(url.toString(), { method: 'POST' });
                        data = await resp.json().catch(()=>null);
                        if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
                      } else if (!resp.ok) {
                        // Проверка на неполный календарь
                        if (data?.error === 'incomplete_calendar') {
                          throw new Error('Не все месяцы заполнены в календаре группы');
                        }
                        throw new Error(data?.error || `HTTP ${resp.status}`);
                      }
                      load();
                      loadCalendar();
                      alert(`Актуализировано карточек: ${data?.updated ?? 0}`);
                    } catch (e) {
                      alert('Ошибка актуализации группы: ' + (e?.message || e));
                    }
                  }}
                  style={{ padding: '6px 10px', borderRadius: 6, background: '#e0e7ff', border: '1px solid #93c5fd' }}
                >Актуализировать все</button>
              )}
              <span style={{ color: '#777' }}>Подписываются только стационарные карточки, назначенные в календаре</span>
            </div>
            <h3 style={{ marginTop: 0 }}>Назначение карточек по месяцам</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Месяц</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Карточка</th>
                </tr>
              </thead>
              <tbody>
                {calendar.months && calendar.months.length ? calendar.months.map((row) => {
                  const qVal = (row._q || '').trim();
                  const all = (row.candidates || []);
                  const filtered = all.filter(c => {
                    if (!qVal) return true;
                    const code = String(c.objectCodeFull || c.objectCode || '').toLowerCase();
                    return code.includes(qVal.toLowerCase());
                  });
                  const assignedCandidate = all.find(c => c.id === row.selectedCardId) || null;
                  const resolvedCandidate = qVal
                    ? (all.find(c => String(c.objectCodeFull).toLowerCase() === qVal.toLowerCase())
                      || all.find(c => String(c.objectCode).toLowerCase() === qVal.toLowerCase())
                      || null)
                    : null;
                  const mismatch = resolvedCandidate && resolvedCandidate.periodMonth != null && resolvedCandidate.periodYear != null
                    ? (Number(resolvedCandidate.periodMonth) !== Number(row.month) || Number(resolvedCandidate.periodYear) !== Number(row.year))
                    : false;
                  const shortFio = (name) => {
                    if (!name) return '';
                    const p = String(name).trim().split(/\s+/);
                    if (p.length === 1) return p[0];
                    const fam = p[0]; const i = p[1] ? (p[1][0] + '.') : ''; const o = p[2] ? (p[2][0] + '.') : '';
                    return [fam, i, o].filter(Boolean).join(' ');
                  };
                  const signedInfo = assignedCandidate?.signedAt ? `Подписано: ${new Date(assignedCandidate.signedAt).toLocaleString()} (${shortFio(assignedCandidate.signedBy) || '—'})` : null;
                  const actualInfo = assignedCandidate?.actualAt ? `Актуализировано: ${new Date(assignedCandidate.actualAt).toLocaleString()} (${shortFio(assignedCandidate.actualBy) || '—'})` : null;
                  const displayVal = (row._q && row._q.length)
                    ? row._q
                    : (assignedCandidate ? (assignedCandidate.objectCodeFull || String(assignedCandidate.objectCode)) : '');
                  const monthKey = `${row.year}-${row.month}`;
                  const isAssigning = assigningKey === monthKey;
                  const isClearing = clearingKey === monthKey;
                  const handleAssign = () => assignCardToMonth(row.month, row.year, resolvedCandidate, mismatch, assignedCandidate);
                  const handleAutoAssign = () => {
                    if (isAssigning || mismatch) return;
                    if (!resolvedCandidate) return;
                    if (assignedCandidate?.id === resolvedCandidate.id) {
                      if (row._q) {
                        setCalendar((prev) => ({
                          months: prev.months.map((m) => (m.year === row.year && m.month === row.month ? { ...m, _q: '' } : m)),
                        }));
                      }
                      return;
                    }
                    assignCardToMonth(row.month, row.year, resolvedCandidate, mismatch, assignedCandidate);
                  };
                  const handleClear = () => clearMonthSelection(row.month, row.year);
                  return (
                    <tr key={`${row.year}-${row.month}`} style={{ background: mismatch ? '#ffecec' : undefined }}>
                      <td style={{ padding: 8 }}>{monthName(row.month)} {row.year}</td>
                      {/* при желании можно сменить отображение месяца: `${monthName(row.month)} ${row.year}` */}
                      <td style={{ padding: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ position: 'relative' }}>
                              <input
                              list={`dl-${row.year}-${row.month}`}
                              placeholder="Поиск и выбор по коду"
                              value={displayVal}
                              onChange={(e)=>{
                                const v = e.target.value;
                                // Обновим локальный ввод
                                setCalendar((prev)=> ({ months: prev.months.map(m => m.year===row.year && m.month===row.month ? { ...m, _q: v } : m) }));
                              }}
                              onKeyDown={(e)=>{
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAssign();
                                }
                              }}
                              onBlur={handleAutoAssign}
                              style={{ padding: '6px 8px', border: `1px solid ${mismatch ? '#c00' : '#ccc'}`, borderRadius: 6, minWidth: 200 }}
                            />
                            <datalist id={`dl-${row.year}-${row.month}`}>
                              {filtered.map(c => {
                                const code = c.objectCodeFull || c.objectCode;
                                const hasP = c.periodMonth != null && c.periodYear != null;
                                const wrong = hasP && (Number(c.periodMonth) !== Number(row.month) || Number(c.periodYear) !== Number(row.year));
                                const label = `${code}${c.status ? ` (${c.status})` : ''}${hasP ? ` — ${String(c.periodMonth).padStart(2,'0')}.${c.periodYear}` : ''}${wrong ? ' — другой месяц' : ''}`;
                                return <option key={c.id} value={code}>{label}</option>
                              })}
                            </datalist>
                          </div>
                          <button
                            onClick={handleAssign}
                            disabled={!resolvedCandidate || isAssigning}
                            style={{ padding: '6px 10px', background: '#e0ecff', border: '1px solid #93c5fd', borderRadius: 6 }}
                          >{isAssigning ? 'Назначаем…' : 'Назначить'}</button>
                          <button
                            onClick={()=>{
                              // Кнопка всегда активна: пытаемся открыть в таком порядке:
                              // 1) назначенная карточка месяца; 2) точное совпадение ввода; 3) первое из отфильтрованных; иначе — предупреждение
                              const pick = assignedCandidate
                                || resolvedCandidate
                                || (filtered.length ? filtered[0] : null);
                              const code = pick?.objectCode;
                              if (code) {
                                window.open(`${window.location.origin}/card-calc/${code}`, '_blank');
                              } else {
                                alert('Не удалось определить карточку для открытия: выберите из списка или назначьте её для месяца');
                              }
                            }}
                            style={{ padding: '6px 10px' }}
                            title={assignedCandidate
                              ? `Открыть ${assignedCandidate.objectCodeFull || assignedCandidate.objectCode}`
                              : (resolvedCandidate ? `Открыть ${resolvedCandidate.objectCodeFull || resolvedCandidate.objectCode}` : 'Открыть карточку')}
                          >Открыть</button>
                          {/* Убрана индивидуальная кнопка актуализации — теперь только одной кнопкой сверху */}
                          <button
                            onClick={()=> setCopyModal({ open: true, objectCode: resolvedCandidate?.objectCode || null, preselect: { month: row.month, year: row.year } })}
                            disabled={!resolvedCandidate}
                            style={{ padding: '6px 10px', background: '#dff0d8', border: '1px solid #4aa33e', borderRadius: 6 }}
                          >Копировать выбранную</button>
                          <button
                            onClick={handleClear}
                            disabled={!assignedCandidate || isClearing}
                            style={{ padding: '6px 10px', background: '#ffecec', border: '1px solid #f19999', borderRadius: 6 }}
                          >{isClearing ? 'Удаляем…' : 'Удалить'}</button>
                          {mismatch && resolvedCandidate && (
                            <span style={{ color: '#c00' }}>
                              Выбранная карточка предназначена для {monthName(resolvedCandidate.periodMonth)} {resolvedCandidate.periodYear}
                            </span>
                          )}
                          {/* убрали надпись "Назначено" — выбранный код отображается прямо в поле */}
                          {filtered.length === 0 && (
                            <span style={{ color: '#777' }}>
                              Нет карточек по этому объекту
                            </span>
                          )}
                          {/* Подписание/Актуализация статусы */}
                          {assignedCandidate && !assignedCandidate.signedAt && (
                            <span style={{ color: '#c00', fontWeight: 600 }}>Не подписано</span>
                          )}
                          {assignedCandidate && assignedCandidate.signedAt && (
                            <span style={{ color: '#2c7a2c' }}>{signedInfo}</span>
                          )}
                          {assignedCandidate && assignedCandidate.actualAt && (
                            <span style={{ color: '#2563eb' }}>{actualInfo}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={2} style={{ padding: 8, textAlign: 'center', color: '#777' }}>Период не задан или нет подходящих карточек</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Блок списка карточек в группе удалён как дублирующий — всё делаем в календаре */}
        </>
      )}

      {/* Сводная таблица открывается как отдельная страница */}
      {copyModal.open && (
        <div
          onClick={() => setCopyModal({ open: false, objectCode: null, preselect: null })}
          style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
        >
          <div
            onClick={(e)=>e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, padding: 16, width: 520, maxWidth: '95%', boxShadow: '0 10px 28px rgba(0,0,0,.25)' }}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Копировать карточку в месяцы</div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
              {(calendar.months || []).map(m => {
                const key = `${m.year}-${m.month}`;
                const checked = copyModal.preselect && copyModal.preselect.year===m.year && copyModal.preselect.month===m.month;
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px' }}>
                    <input
                      type="checkbox"
                      checked={!!(copyModal[`sel_${key}`] ?? checked)}
                      onChange={(e)=> setCopyModal(s => ({ ...s, [`sel_${key}`]: e.target.checked }))}
                    />
                    <span>{monthName(m.month)} {m.year}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setCopyModal({ open: false, objectCode: null, preselect: null })}>Отмена</button>
              <button
                onClick={() => {
                  const months = (calendar.months || [])
                    .filter(m => copyModal[`sel_${m.year}-${m.month}`] || (copyModal.preselect && copyModal.preselect.year===m.year && copyModal.preselect.month===m.month))
                    .map(m => ({ month: m.month, year: m.year }));
                  if (!months.length) { alert('Выберите месяцы'); return; }
                  const oc = copyModal.objectCode;
                  setCopyModal({ open: false, objectCode: null, preselect: null });
                  copyCardToMonths(oc, months);
                }}
                style={{ padding: '6px 10px', background: '#dff0d8', border: '1px solid #4aa33e', borderRadius: 6 }}
              >Скопировать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Компонент SummaryTable удалён: сводная таблица вынесена на отдельную страницу

// formatVal удалён — форматирование встроено в SummaryTable
